'use client'

import { useMemo } from 'react'

import Link from 'next/link'
import { usePathname } from 'next/navigation'

import {
  useAccessToken,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  CategoriesSubmenu,
  ConditionsSubmenu,
  FormattedMessage,
  HeaderAuctionNavigation,
  RegionsSubmenu,
  SellersSubmenu,
  type AuctionNavDropdownSection,
  type AuctionNavInlineLink,
  type AuctionNavLinkProps,
  type AuctionNavMenuItem,
  type AuctionNavMenuGroup,
  type AuctionNavRenderLink,
} from '@b-stock/fe-core/bstock-react'

import type { MarketplaceAuctionNav } from '@/helpers/cms/marketplaceNav'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useGetAllSellers } from '@/helpers/useGetAllSellers'

import categoriesData from './auctionNavData/categoriesData'
import { conditionsData } from './auctionNavData/conditionsData'
import regionsData from './auctionNavData/regionsData'
import { useAuctionNavAnalytics } from './useAuctionNavAnalytics'

/**
 * Renders the relocated, presentational `HeaderAuctionNavigation` from fe-core
 * with home-portal-owned data + Next.js routing.
 *
 * No option removals are applied — the dropdowns carry the same data set as the
 * legacy nav (BP-1938 decision: defer removals until the fe-core components are
 * fully adopted).
 */

// fe-core's HeaderAuctionNavigation is router-agnostic: it renders internal
// links through this component so Next.js client navigation keeps working.
// next/link forwards className (styled-components), onClick, and aria-current
// to the underlying anchor, which is exactly what fe-core requires.
const NextNavLink: AuctionNavRenderLink = ({
  href,
  children,
  ...rest
}: AuctionNavLinkProps) => (
  <Link href={href} {...rest}>
    {children}
  </Link>
)

const toMenuItem = (item: {
  name: string
  href: string
}): AuctionNavMenuItem => ({
  name: item.name,
  href: item.href,
})

const toMenuGroup = (group: {
  header: string
  items: { name: string; href: string }[]
  footer?: { name: string; href: string }
}): AuctionNavMenuGroup => ({
  header: group.header,
  items: group.items.map(toMenuItem),
  ...(group.footer ? { footer: toMenuItem(group.footer) } : {}),
})

// Sellers data is fetched at render time (auth-aware), so its submenu content
// needs its own component to call the query hook.
function SellersSubmenuContent() {
  const accessToken = useAccessToken()
  const { data: allSellersData = [], isLoading } = useGetAllSellers(accessToken)

  if (isLoading) {
    return null
  }

  const groups: AuctionNavMenuGroup[] = allSellersData.map((group) => ({
    header: group.header,
    items: group.items.map((item) => ({
      name: item.name,
      href: item.href,
      external: item.linkToStorefront,
    })),
  }))

  return <SellersSubmenu groups={groups} renderLink={NextNavLink} />
}

export default function GlobalHeaderAuctionNavigation({
  cms,
}: {
  /**
   * Nav content from the CMS, per section. Absent sections fall back to the committed data files
   * below, so a half-filled document keeps the dropdowns it hasn't reached yet.
   */
  cms?: MarketplaceAuctionNav
} = {}) {
  const pathname = usePathname()
  const onAnalyticsEvent = useAuctionNavAnalytics()

  const tvGuideScheduleEnabled = useFlag(FeatureFlags.ENABLE_TV_GUIDE_SCHEDULE)
  const decodedAccessToken = useDecodedAccessToken()

  const inlineLinks: AuctionNavInlineLink[] | undefined =
    tvGuideScheduleEnabled && decodedAccessToken
      ? [
          {
            key: 'auction-schedule',
            label: <FormattedMessage id="AuctionSchedule.navLabel" />,
            href: '/buy/auction-schedule',
          },
        ]
      : undefined

  const categoryItems = useMemo<AuctionNavMenuItem[]>(
    () => cms?.categories ?? categoriesData.map(toMenuItem),
    [cms?.categories]
  )

  const conditionItems = useMemo<AuctionNavMenuItem[]>(
    () => cms?.conditions ?? conditionsData.map(toMenuItem),
    [cms?.conditions]
  )

  const regionGroups = useMemo<AuctionNavMenuGroup[]>(
    () => cms?.regions ?? regionsData.map(toMenuGroup),
    [cms?.regions]
  )

  const sections: AuctionNavDropdownSection[] = [
    {
      key: 'categories',
      label: cms?.categoriesLabel ?? 'All Categories',
      content: (
        <CategoriesSubmenu items={categoryItems} renderLink={NextNavLink} />
      ),
    },
    {
      key: 'sellers',
      label: cms?.sellersLabel ?? 'All Sellers',
      align: 'center',
      // Label only — the list comes from the seller service at render.
      content: <SellersSubmenuContent />,
    },
    {
      key: 'conditions',
      label: cms?.conditionsLabel ?? 'All Conditions',
      content: (
        <ConditionsSubmenu items={conditionItems} renderLink={NextNavLink} />
      ),
    },
    {
      key: 'regions',
      label: cms?.regionsLabel ?? 'All Regions',
      content: (
        <RegionsSubmenu groups={regionGroups} renderLink={NextNavLink} />
      ),
    },
  ]

  return (
    <HeaderAuctionNavigation
      allAuctions={{ label: 'All Auctions', href: '/all-auctions' }}
      inlineLinks={inlineLinks}
      sections={sections}
      activePath={pathname ?? undefined}
      renderLink={NextNavLink}
      onAnalyticsEvent={onAnalyticsEvent}
    />
  )
}
